'use client';

import './connections.css';
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import type { BoardResponse } from '../lib/types';
import { matchSelection, shareGrid } from '../lib/select';
import {
  MAX_MISTAKES,
  MAX_HINTS,
  finalScore,
  nextHintCost,
} from '../lib/score';
import { ConnectionsDistribution } from './ConnectionsDistribution';
import { useShareResult } from '@/lib/gameShare';
import { KeepPlayingFooter } from '../../_components/KeepPlayingFooter';
import { DailySlateNav } from '../../_components/DailySlateNav';

// useLayoutEffect warns during SSR; fall back to useEffect on the server.
const useIsoLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

// Band colors keep their difficulty hues (the 🟨🟩🟦🟪 share-emoji mapping
// depends on them staying recognizably yellow/green/blue/purple); yellow is
// one step deeper than the original #f4c430 so it doesn't go neon on the
// scorecard-sand surface.
const BAND_COLOR: Record<1 | 2 | 3 | 4, string> = {
  1: '#e5b32e', // yellow
  2: '#5aa469', // green
  3: '#4a78c4', // blue
  4: '#8a63b8', // purple
};

// Shared button styling — uses the design-theme sans (native <button> does not
// inherit font-family, so it must be set explicitly).
const BTN_BASE: CSSProperties = {
  fontFamily: 'var(--font-sans)',
  fontWeight: 600,
  fontSize: 14,
  padding: '9px 16px',
  borderRadius: 8,
  cursor: 'pointer',
};
const BTN_GHOST: CSSProperties = {
  ...BTN_BASE,
  border: '1px solid var(--cx-rule)',
  background: 'var(--cx-card)',
  color: 'var(--cx-ink)',
};
const BTN_PRIMARY: CSSProperties = {
  ...BTN_BASE,
  border: '1px solid var(--coral-deep)',
  background: 'var(--coral-deep)',
  color: '#fff',
};

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export function ConnectionsBoard({
  board,
  userId,
}: {
  board: BoardResponse;
  userId?: string;
}) {
  const { puzzle } = board;
  const groups = puzzle.groups;

  const [tiles, setTiles] = useState<string[]>(() => shuffle(puzzle.tiles));
  const [selected, setSelected] = useState<string[]>([]);
  const [solvedIds, setSolvedIds] = useState<string[]>(
    board.result ? groups.map((g) => g.categoryId) : [],
  );
  const [failed, setFailed] = useState<boolean>(
    board.result ? !board.result.solved : false,
  );
  const [mistakes, setMistakes] = useState(board.result?.mistakes ?? 0);
  const [hintsUsed, setHintsUsed] = useState(board.result?.hints ?? 0);
  // Category ids whose theme has been revealed by a hint (in-play only).
  const [revealedIds, setRevealedIds] = useState<string[]>([]);
  const [log, setLog] = useState<string[][]>(board.result?.guessesLog ?? []);
  const [toast, setToast] = useState<string | null>(null);

  // FLIP: tiles keep stable keys, so on shuffle (or when a solved row is
  // removed) React reorders the same DOM nodes; we measure layout positions
  // before/after and slide each from its old spot to its new one.
  const tileEls = useRef<Map<string, HTMLButtonElement>>(new Map());
  const prevPos = useRef<Map<string, { l: number; t: number }>>(new Map());

  const won = solvedIds.length === 4 && !failed;
  const lost = failed;
  const done = won || lost;

  const solvedGroups = useMemo(
    () => solvedIds.map((id) => groups.find((g) => g.categoryId === id)!),
    [solvedIds, groups],
  );
  const remainingTiles = tiles.filter(
    (t) => !solvedGroups.some((g) => g.members.includes(t)),
  );

  // Hints reveal a still-unsolved group's theme (not its tiles). Targets are
  // unsolved groups not yet revealed; spending is capped at MAX_HINTS.
  const hintTargets = groups.filter(
    (g) =>
      !solvedIds.includes(g.categoryId) && !revealedIds.includes(g.categoryId),
  );
  const canHint = !done && hintsUsed < MAX_HINTS && hintTargets.length > 0;
  // Revealed groups still in play (once solved, the colored band shows the
  // title anyway, so we stop showing the hint banner for it).
  const revealedGroups = groups.filter(
    (g) =>
      revealedIds.includes(g.categoryId) && !solvedIds.includes(g.categoryId),
  );

  // Runs after every render. offsetLeft/Top are layout positions (they ignore
  // the entrance transform), so the cascade never triggers a spurious slide.
  useIsoLayoutEffect(() => {
    const reduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    const cur = new Map<string, { l: number; t: number }>();
    tileEls.current.forEach((el, tile) =>
      cur.set(tile, { l: el.offsetLeft, t: el.offsetTop }),
    );
    if (!reduced) {
      cur.forEach((pos, tile) => {
        const prev = prevPos.current.get(tile);
        const el = tileEls.current.get(tile);
        if (!prev || !el) return;
        const dx = prev.l - pos.l;
        const dy = prev.t - pos.t;
        if (dx || dy) {
          el.style.transition = 'none';
          el.style.transform = `translate(${dx}px, ${dy}px)`;
          requestAnimationFrame(() => {
            el.style.transition =
              'transform 0.4s cubic-bezier(0.2, 0.7, 0.2, 1)';
            el.style.transform = '';
          });
        }
      });
    }
    prevPos.current = cur;
  });

  // Rehydrate a completed board on revisit. Signed-in players get board.result
  // from the server; anonymous completions live only in localStorage (the
  // server can't read the browser session key at SSR), so seed the solved/failed
  // state from the cache before paint. Runs once — the deps are all stable props.
  useIsoLayoutEffect(() => {
    if (board.result) return;
    try {
      const raw = window.localStorage.getItem(`cx_result_${puzzle.id}`);
      if (!raw) return;
      const saved = JSON.parse(raw) as {
        solved: boolean;
        mistakes: number;
        hints?: number;
        guessesLog: string[][];
      };
      setSolvedIds(groups.map((g) => g.categoryId));
      setFailed(!saved.solved);
      setMistakes(Math.min(Math.max(saved.mistakes ?? 0, 0), MAX_MISTAKES));
      setHintsUsed(Math.min(Math.max(saved.hints ?? 0, 0), MAX_HINTS));
      setLog(saved.guessesLog ?? []);
    } catch {
      /* corrupt cache — ignore, play fresh */
    }
  }, [board.result, puzzle.id, groups]);

  function toggle(tile: string) {
    if (done) return;
    setSelected((s) =>
      s.includes(tile)
        ? s.filter((t) => t !== tile)
        : s.length < 4
          ? [...s, tile]
          : s,
    );
  }

  // Shake the currently-selected tiles on an invalid guess (added imperatively
  // so it doesn't churn the React className / re-trigger the entrance).
  function shakeSelected() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    selected.forEach((tile) =>
      tileEls.current.get(tile)?.classList.add('cx-shake'),
    );
  }

  // Spend a hint: reveal the easiest unsolved-and-unrevealed group's theme.
  function takeHint() {
    if (!canHint) return;
    const target = [...hintTargets].sort(
      (a, b) => a.difficulty - b.difficulty,
    )[0];
    setRevealedIds((r) => [...r, target.categoryId]);
    setHintsUsed((h) => h + 1);
  }

  async function submit() {
    if (selected.length !== 4) return;
    const result = matchSelection(selected, groups);
    const nextLog = [...log, selected];
    setLog(nextLog);

    if (result.kind === 'correct') {
      const nextSolved = [...solvedIds, result.categoryId];
      setSolvedIds(nextSolved);
      setSelected([]);
      if (nextSolved.length === 4) await finish(true, mistakes, nextLog);
    } else {
      shakeSelected();
      const nextMistakes = mistakes + 1;
      setMistakes(nextMistakes);
      setToast(result.kind === 'one-away' ? 'One away…' : 'Not a group');
      setTimeout(() => setToast(null), 1400);
      if (nextMistakes >= MAX_MISTAKES) {
        setFailed(true);
        setSolvedIds(groups.map((g) => g.categoryId)); // reveal all
        await finish(false, nextMistakes, nextLog);
      }
    }
  }

  async function finish(
    solved: boolean,
    finalMistakes: number,
    finalLog: string[][],
  ) {
    try {
      const headers: Record<string, string> = {
        'content-type': 'application/json',
      };
      if (userId) headers['X-User-Id'] = userId;
      await fetch('/api/spot/connections/result', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          puzzleId: puzzle.id,
          sessionKey: getSessionKey(),
          solved,
          mistakes: finalMistakes,
          hintsUsed,
          guessesLog: finalLog,
        }),
      });
    } catch {
      /* result is best-effort; the game already played out client-side */
    }
    // Cache the completed result locally so a revisit re-shows the solved board
    // even for anonymous players (the server only knows results for signed-in
    // users — it can't read the browser session key at SSR). See the rehydrate
    // effect below.
    try {
      window.localStorage.setItem(
        `cx_result_${puzzle.id}`,
        JSON.stringify({
          solved,
          mistakes: finalMistakes,
          hints: hintsUsed,
          guessesLog: finalLog,
        }),
      );
    } catch {
      /* private mode / quota — non-fatal */
    }
  }

  return (
    <main className="cx-stage">
      <header className="cx-head">
        <div className="cx-kicker">car connections · mondays</div>
        <h1 className="cx-title">
          Car <em>Connections</em>
        </h1>
        {puzzle.theme && (
          <div className="cx-theme">
            This week&rsquo;s theme
            <strong>{puzzle.theme}</strong>
          </div>
        )}
        <div className="cx-banner">
          16 cars · 4 groups of 4 · {MAX_MISTAKES} mistakes
        </div>
        {!done && (
          <div className="cx-rules">
            <span>
              <b>Find the four hidden groups of four.</b> Every group ties into
              the theme.
            </span>
            <span>
              <b>{MAX_MISTAKES} mistakes allowed</b> — 💡 hints reveal a
              group&rsquo;s title, for points.
            </span>
          </div>
        )}
      </header>

      <div className="cx-board">
        {solvedGroups.map((g) => (
          <div
            key={g.categoryId}
            className="cx-band"
            style={{
              background: BAND_COLOR[g.difficulty],
              borderRadius: 10,
              padding: '10px 12px',
              marginBottom: 8,
              aspectRatio: '4 / 1',
              minHeight: 56,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              color: '#1a1a1a',
              textAlign: 'center',
            }}
          >
            <strong
              style={{
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                fontWeight: 800,
                fontSize: 15,
                lineHeight: 1.1,
              }}
            >
              {g.title}
            </strong>
            <div
              style={{
                fontSize: 14,
                fontWeight: 400,
                marginTop: 3,
                lineHeight: 1.2,
              }}
            >
              {g.members.join(', ')}
            </div>
            <div
              style={{
                fontSize: 12,
                marginTop: 4,
                opacity: 0.8,
                fontStyle: 'italic',
                fontWeight: 300,
                lineHeight: 1.25,
              }}
            >
              {g.fact}
            </div>
          </div>
        ))}

        {revealedGroups.length > 0 && (
          <div style={{ marginBottom: 8 }}>
            {revealedGroups.map((g) => (
              <div
                key={g.categoryId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 12px',
                  marginBottom: 6,
                  borderRadius: 10,
                  border: `1px dashed ${BAND_COLOR[g.difficulty]}`,
                  background: 'var(--cx-card)',
                  fontSize: 13,
                }}
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: BAND_COLOR[g.difficulty],
                    flexShrink: 0,
                  }}
                />
                <span style={{ opacity: 0.6 }}>Hint —</span>
                <strong style={{ fontWeight: 700 }}>{g.title}</strong>
              </div>
            ))}
          </div>
        )}

        {!done && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 8,
            }}
          >
            {remainingTiles.map((tile, i) => {
              const isSel = selected.includes(tile);
              return (
                <button
                  key={tile}
                  ref={(el) => {
                    if (el) tileEls.current.set(tile, el);
                    else tileEls.current.delete(tile);
                  }}
                  onClick={() => toggle(tile)}
                  onAnimationEnd={(e) => {
                    // Drop the entrance class once it finishes so its filled
                    // transform stops fighting the FLIP transform; clear the
                    // shake class so it can re-fire on the next bad guess.
                    if (e.animationName === 'cx-tile-in')
                      e.currentTarget.classList.remove('cx-tile');
                    if (e.animationName === 'cx-shake')
                      e.currentTarget.classList.remove('cx-shake');
                  }}
                  className="cx-tile cx-word"
                  data-selected={isSel || undefined}
                  style={{ animationDelay: `${i * 35}ms` }}
                >
                  {tile}
                </button>
              );
            })}
          </div>
        )}

        <div className="cx-status">
          <span aria-live="polite" style={{ minHeight: 20, fontWeight: 600 }}>
            {toast}
          </span>
          <span>
            Mistakes remaining:{' '}
            {Array.from({ length: MAX_MISTAKES }).map((_, i) => (
              <span
                key={i}
                style={{ opacity: i < MAX_MISTAKES - mistakes ? 1 : 0.2 }}
              >
                ●
              </span>
            ))}
          </span>
        </div>

        {!done && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginTop: 12,
            }}
          >
            <button
              onClick={takeHint}
              disabled={!canHint}
              style={{ ...BTN_GHOST, opacity: canHint ? 1 : 0.45 }}
            >
              💡 Reveal a group
              {hintsUsed < MAX_HINTS ? ` · −${nextHintCost(hintsUsed)}` : ''}
            </button>
            <span style={{ fontSize: 13, opacity: 0.7 }}>
              {MAX_HINTS - hintsUsed} of {MAX_HINTS} left
            </span>
          </div>
        )}

        {!done && (
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button onClick={() => setTiles(shuffle(tiles))} style={BTN_GHOST}>
              Shuffle
            </button>
            <button
              onClick={() => setSelected([])}
              disabled={!selected.length}
              style={{ ...BTN_GHOST, opacity: selected.length ? 1 : 0.45 }}
            >
              Deselect all
            </button>
            <button
              onClick={submit}
              disabled={selected.length !== 4}
              style={{
                ...BTN_PRIMARY,
                marginLeft: 'auto',
                opacity: selected.length === 4 ? 1 : 0.45,
              }}
            >
              Submit
            </button>
          </div>
        )}

        {done && (
          <>
            <ShareRow
              won={won}
              mistakes={mistakes}
              hints={hintsUsed}
              date={board.date}
              grid={shareGrid(log, groups)}
            />
            <ConnectionsDistribution
              date={board.date}
              yourScore={finalScore(mistakes, hintsUsed)}
            />
            <KeepPlayingFooter
              game="connections"
              date={board.date}
              userId={userId}
              archiveHref="/games?game=connections"
              includeSelf
            />
          </>
        )}
        <DailySlateNav
          currentGame="connections"
          date={board.date}
          userId={userId}
        />
      </div>
    </main>
  );
}

function ShareRow({
  won,
  mistakes,
  hints,
  date,
  grid,
}: {
  won: boolean;
  mistakes: number;
  hints: number;
  date: string;
  grid: string;
}) {
  // Final score = max(0, (9 - mistakes) - triangular(hints)). See lib/score.ts.
  const score = finalScore(mistakes, hints);
  const hintTag = hints > 0 ? ` · 💡×${hints}` : '';
  const text = `Car Connections ${date}\n${
    won ? 'Solved' : 'Did not solve'
  } · ${score}/9${hintTag}\n${grid}\nhttps://namethecar.com/${date}`;
  const { share, copied } = useShareResult('connections', text);
  return (
    <div style={{ marginTop: 16, textAlign: 'center' }}>
      <p className="cx-verdict">{won ? 'Solved! 🎉' : 'Out of guesses'}</p>
      <p style={{ marginTop: 2, fontWeight: 600 }}>
        {score} / 9 points
        {hints > 0 ? (
          <span style={{ fontWeight: 400, opacity: 0.7 }}>
            {' '}
            · {hints} hint{hints > 1 ? 's' : ''}
          </span>
        ) : null}
      </p>
      <pre style={{ fontSize: 18, lineHeight: 1.2 }}>{grid}</pre>
      <button onClick={share} style={BTN_PRIMARY}>
        {copied ? 'Copied!' : 'Share result'}
      </button>
    </div>
  );
}

// Anonymous session key, persisted in localStorage (parity with SPOT's anon flow).
function getSessionKey(): string {
  if (typeof window === 'undefined') return 'ssr';
  let k = window.localStorage.getItem('cx_session_key');
  if (!k) {
    k = crypto.randomUUID();
    window.localStorage.setItem('cx_session_key', k);
  }
  return k;
}
